import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { dateFormat, currencySymbol } from '../../../../../utils/constants';
import { subscriptionAPI } from '../../../../../services/redwolfSdkAPI';
import * as Sentry from '@sentry/react';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import InvoiceSkeleton from '../../../../common/skeletons/InvoiceSkeleton';

const MyInvoice = () => {
	const getQueryParamsValue = new URLSearchParams(useLocation().search);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(true);
	const [purchasedOrderDetails, setpurchasedOrderDetails]: any = useState([]);
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();

	const transaction = Sentry.startInactiveSpan({ name: 'My Invoice' });

	useEffect(() => {
		document.body.style.padding = 'unset !important';
		document.title = 'My Invoice | VIZIO';
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VIEW_RECEIPT');
		getMyInvoice();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getMyInvoice = () => {
		const invoiceId = getQueryParamsValue.get('invoiceid');
		const token = getToken();

		if (invoiceId) {
			requestRedwolfService(subscriptionAPI.getV2InvoiceById(token, invoiceId))
				.then((sdkResponse: any) => {
					if (sdkResponse.constructor.$metadata$.simpleName === 'Success') {
						setIsSkeletonLoading(false);

						setpurchasedOrderDetails(sdkResponse.data);
					} else {
						setIsSkeletonLoading(false);
					}
					transaction && transaction.end();
				})
				.catch((err: any) => {
					setIsSkeletonLoading(false);
					transaction && transaction.end();
					Sentry.captureException(err);
				});
		}
	};

	let items = null;
	let payment = null;
	let credit = null;
	let billedOn = null;

	if (purchasedOrderDetails.items) {
		items = purchasedOrderDetails.items.toArray();
	}
	if (purchasedOrderDetails.payments) {
		payment = purchasedOrderDetails.payments.toArray()[0];
	}
	if (purchasedOrderDetails) {
		credit = purchasedOrderDetails.credits;
	}
	if (purchasedOrderDetails.billingDate) {
		const billingDate = new Date(purchasedOrderDetails.billingDate);
		billedOn = format(billingDate, dateFormat);
	}

	let purchaseText = '';
	let purchasesText = '';

	if (purchasedOrderDetails.isRental === false) {
		purchaseText = 'purchase';
		purchasesText = 'purchases';
	} else if (purchasedOrderDetails.isRental === true) {
		purchaseText = 'rental';
		purchasesText = 'rentals';
	}
	if (purchasedOrderDetails.isSubscription === true) {
		purchaseText = 'subscription';
		purchasesText = 'subscriptions';
	}

	return (
		<>
			<div className="subs-tmp-component">
				<div className="tem-logo">
					<img src="images/vizio-logo-print.svg" alt="logo" className="logo-img" />
				</div>

				<div className="s__template-info">
					<p className="text">Thank you for your {purchaseText}.</p>

					{isSkeletonLoading ? (
						<InvoiceSkeleton />
					) : (
						<>
							<div className="inv-hd">
								<span className="inv-no">Invoice #{purchasedOrderDetails.invoiceNumber}</span>
								<span className="inv-date">{billedOn}</span>
							</div>

							<div className="device-row">
								{items &&
									items.map((item: any, index: number) => {
										if (item.isAddOn) {
											return (
												<div className="add-on-infom" key={index}>
													<div className="invoice-add-on-container">
														<span className="invoice-info-icon">+</span>
														<span className="invoice-addon-title">{item.description}</span>
													</div>
													<div className="invoice-addon-price">
														<span className="invoice-offer-info">
															{item.price === 0 && '(Free Trial)'}
														</span>
														<span className="invoice-offer-price">
															{currencySymbol + Number(item.price).toFixed(2)}
														</span>
													</div>
												</div>
											);
										} else {
											return (
												<div className="device-items" key={index}>
													<div className="device-info">
														<div className="device-icon">
															<img src={item.appImageUrl} alt="App logo" />
														</div>
														<div className="device-cnt">
															<h3>{item.appName}</h3>
															<p>{item.description}</p>
														</div>
													</div>
													<div className="device-price">
														<span className="price">
															{currencySymbol + Number(item.price).toFixed(2)}
														</span>
													</div>
												</div>
											);
										}
									})}

								{purchasedOrderDetails.discount != 0 && (
									<div className="discount-container">
										<div className="discount-txt">Discount Applied</div>
										<div className="discount-price">
											({currencySymbol + Number(purchasedOrderDetails.discount).toFixed(2)})
										</div>
									</div>
								)}
							</div>

							<div className="s-total-block">
								<ul>
									<li>
										<span className="s-tt">Subtotal</span>
										<span className="stt-price">
											{currencySymbol + Number(purchasedOrderDetails.subtotal).toFixed(2)}
										</span>
									</li>
									<li>
										<span className="s-tt">Tax</span>
										<span className="stt-price">
											{currencySymbol + Number(purchasedOrderDetails.tax).toFixed(2)}
										</span>
									</li>
									<li>
										<span className="s-tt s-total">Total</span>
										<span className="stt-price s-total-price">
											{currencySymbol + Number(purchasedOrderDetails.total).toFixed(2)}
										</span>
									</li>
									<li>
										<span className="s-tt">Credit Applied</span>
										<span className="stt-price">
											({credit ? currencySymbol + Number(credit).toFixed(2) : '$0.00'})
										</span>
									</li>

									<li>
										<span className="s-tt">Paid</span>
										<span className="stt-price">
											({currencySymbol + Number(purchasedOrderDetails.paid).toFixed(2)})
										</span>
									</li>

									<li>
										<span className="s-tt s-amount-due">Amount Due</span>
										<span className="stt-price s-amount-due-price">
											{currencySymbol + Number(purchasedOrderDetails.amountDue).toFixed(2)}
										</span>
									</li>
								</ul>
							</div>

							<div className="tem-payment">
								<div className="tm-p-title">
									<span className="tile">Payment Method</span>
								</div>
								<div className="tm-pay-info">
									<span className="p-icon">{purchasedOrderDetails.cardTypeCode}</span>
									<span className="p-cc-n">...{purchasedOrderDetails.cardLastFour}</span>
								</div>
							</div>
						</>
					)}
				</div>

				<div className="s__template-modify">
					<p>
						To modify or make changes to your {purchasesText}, visit{' '}
						<a href="/en/account" target="_blank" rel="noreferrer">
							vizio.com/account
						</a>
					</p>
				</div>
			</div>
		</>
	);
};

export default MyInvoice;
